import { useRouterState } from "@tanstack/react-router";
import { detectLocale, withLocale, stripLocale } from "@/lib/i18n/locale";

/**
 * EN | ES language switcher.
 *
 * Uses plain <a> (not <Link>) so the browser does a full navigation — the
 * new locale gets a fresh SSR render with the correct <html lang> and
 * hreflang tags, which is the correct behaviour for a language change.
 */
export function LanguageSwitcher({
  variant = "light",
}: {
  variant?: "light" | "dark";
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locale = detectLocale(pathname);
  const base = stripLocale(pathname);
  const enHref = withLocale(base, "en");
  const esHref = withLocale(base, "es");

  const active = variant === "light" ? "text-accent" : "text-accent";
  const inactive =
    variant === "light"
      ? "text-foreground/50 hover:text-foreground"
      : "text-white/50 hover:text-white";

  return (
    <div
      className="flex items-center gap-1.5 text-xs font-bold"
      aria-label="Language selector"
    >
      <a
        href={enHref}
        className={`transition-colors ${locale === "en" ? active : inactive}`}
        aria-label="English"
        aria-current={locale === "en" ? "true" : undefined}
      >
        EN
      </a>
      <span
        className={variant === "light" ? "text-foreground/30" : "text-white/30"}
      >
        |
      </span>
      <a
        href={esHref}
        className={`transition-colors ${locale === "es" ? active : inactive}`}
        aria-label="Español"
        aria-current={locale === "es" ? "true" : undefined}
      >
        ES
      </a>
    </div>
  );
}

export default LanguageSwitcher;
